import React, { FC, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap';
import {
  DealerFullModel,
  SeatingPreference,
  VendorPreferenceState,
} from '../../../../shared/dealer';
import {
  AccessDenied,
  ActionButton,
  LinkButton,
  MaterialIcon,
  RichMarkdownIt,
  UserStateComponent,
} from '../../../components';
import { JSONForm } from '../../../components/json-form/JSONForm';
import { useTranslation } from '../../../translations';
import { useFetcher } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';

import './VendorSeating.scss';

interface StateStyle {
  icon: string;
  type: string;
  message: string;
  color: string;
}

interface VendorSeatingComponentProps {
  readonly dealer?: DealerFullModel;
}

export const VendorSeatingComponent: FC<VendorSeatingComponentProps> = ({ dealer }) => {
  return (
    <UserStateComponent>
      {dealer ? <VendorSeatingComponentInner dealer={dealer} /> : <AccessDenied />}
    </UserStateComponent>
  );
};

interface VendorSeatingComponentInnerProps {
  readonly dealer: DealerFullModel;
}

const VendorSeatingComponentInner: FC<VendorSeatingComponentInnerProps> = ({ dealer }) => {
  const request = useFetcher(async () => {
    return await api.getDealerSeatingPreferences(dealer.id);
  }, [dealer.id]);

  const { ts } = useTranslation();
  return (
    <Row className="justify-content-center" id="dealerSeatingForm">
      <Col className="mb-10 justify-content-start" lg={3} xs={12}>
        <LinkButton block to="/vendor">
          {ts('back_to_application')}
        </LinkButton>
      </Col>
      <Col lg={9} xs={12} />
      <Col lg={4} xs={12}>
        <Card className="mb-10">
          <CardBody>
            <CardTitle>{ts('how_it_works')}</CardTitle>
            <RichMarkdownIt source={ts('your_unique_vendor_id_can')} />
            <hr />
            <FormGroup>
              <Label for="inviteCode">{ts('your_vendor_id')}</Label>
              <Input id="inviteCode" readOnly value={dealer.publicID} />
            </FormGroup>
          </CardBody>
        </Card>
      </Col>
      <Col lg={8} xs={12}>
        {request.success && <AddVendor dealer={dealer} reload={request.refresh} />}
        {request.success && (
          <VendorSeatingRequests
            dealer={dealer}
            reload={request.refresh}
            seatingPreferences={request.data}
          />
        )}
      </Col>
    </Row>
  );
};

interface AddVendorProps {
  readonly dealer: DealerFullModel;
  readonly reload: () => void;
}

const AddVendor: FC<AddVendorProps> = ({ dealer, reload }) => {
  const { ts } = useTranslation();
  const onSuccess = useCallback(() => {
    reload();
    toast.success(ts('we_have_added_your_seating'));
  }, [reload, ts]);

  return (
    <Card className="mb-10">
      <CardBody>
        <CardTitle>{ts('add_a_vendor')}</CardTitle>
        <p>{ts('if_another_vendor_has_provided')}</p>
        <hr />
        <JSONForm
          method="post"
          onSuccess={onSuccess}
          path={`/api/dealers/${dealer.id}/seatingPreferences`}
        >
          <FormGroup row>
            <Label for="token" lg={2} md={4} sm={12}>
              {ts('vendor_id')}
            </Label>
            <Col className="mb-10" md={8} sm={12}>
              <Input id="token" name="token" />
            </Col>
            <Col lg={2} sm={12}>
              <Button block color="primary" id="addSeatingPreference" type="submit">
                {ts('common.add')}
              </Button>
            </Col>
          </FormGroup>
        </JSONForm>
      </CardBody>
    </Card>
  );
};

interface ExistingRequestsProps {
  readonly dealer: DealerFullModel;
  readonly seatingPreferences: SeatingPreference[];
  readonly reload: () => void;
}

export const VendorSeatingRequests: FC<ExistingRequestsProps> = ({
  dealer,
  seatingPreferences,
  reload,
}) => {
  const { ts } = useTranslation();
  // Sort by own requests first, then by approved, then by requested
  const sortedSeatingPreferences = seatingPreferences.sort((a, b) => {
    if (a.requester.id === dealer.id && b.requester.id !== dealer.id) {
      return -1;
    }

    if (a.requester.id !== dealer.id && b.requester.id === dealer.id) {
      return 1;
    }

    if (a.status === 'approved' && b.status !== 'approved') {
      return -1;
    }

    if (a.status !== 'approved' && b.status === 'approved') {
      return 1;
    }

    if (a.status === 'requested' && b.status !== 'requested') {
      return -1;
    }

    if (a.status !== 'requested' && b.status === 'requested') {
      return 1;
    }

    return 0;
  });

  return (
    <Card>
      <CardBody>
        <CardTitle>{ts('seating_requests')}</CardTitle>
        {sortedSeatingPreferences.length === 0 && (
          <p className="text-muted">{ts('no_existing_requests')}</p>
        )}
        {sortedSeatingPreferences.length > 0 && (
          <Row className="justify-content-center">
            <Table>
              <tr>
                <th>{ts('dealer')}</th>
                <th>{ts('common.status')}</th>
                <th style={{ maxWidth: '250px' }} />
              </tr>
              {sortedSeatingPreferences.map((pref) => {
                return (
                  <DealerPreferenceRow
                    dealer={dealer}
                    key={`${pref.requester.id}-${pref.target.id}`}
                    pref={pref}
                    reload={reload}
                  />
                );
              })}
            </Table>
          </Row>
        )}
      </CardBody>
    </Card>
  );
};

interface DealerPreferenceRowProps {
  readonly dealer: DealerFullModel;
  readonly pref: SeatingPreference;
  readonly reload: () => void;
}

const DealerPreferenceRow: FC<DealerPreferenceRowProps> = ({ dealer, pref, reload }) => {
  const isOutgoingRequest = pref.requester.id === dealer.id;
  const { name, id } = isOutgoingRequest ? pref.target : pref.requester;
  const { color, icon, message } = getStateStyle(pref.status);

  const { ts } = useTranslation();
  return (
    <tr className="dealer-preference-row">
      <td>
        <div>
          <MaterialIcon name={isOutgoingRequest ? 'call_made' : 'call_received'} />
          {name}
        </div>
      </td>
      <td className="dealer-preference-status" id={`status-${id}-${pref.status}`}>
        <Badge color={color}>
          <MaterialIcon name={icon} small />
          <span>{message}</span>
        </Badge>
      </td>
      <td className="dealer-preference-actions">
        {pref.status === 'requested' && !isOutgoingRequest && (
          <ActionButton
            action={async () => {
              await api.setDealerSeatingPreferenceStatus(dealer.id, id, 'approve');
            }}
            color="primary"
            id={`request-${id}-approve`}
            onFailure={captureError}
            onSuccess={() => {
              toast.success(ts('seating_preference_request_approved'));
              reload();
            }}
          >
            {ts('common.approve')}
          </ActionButton>
        )}
        <ActionButton
          action={async () => {
            await api.setDealerSeatingPreferenceStatus(dealer.id, id, 'reject');
          }}
          color="danger"
          id={`request-${id}-delete`}
          onFailure={captureError}
          onSuccess={() => {
            toast.success(
              ts(
                isOutgoingRequest
                  ? 'seating_preference_request_cancelled'
                  : 'seating_preference_request_declined',
              ),
            );

            reload();
          }}
        >
          {isOutgoingRequest ? 'Cancel' : 'Decline'}
        </ActionButton>
      </td>
    </tr>
  );
};

function getStateStyle(state: VendorPreferenceState): StateStyle {
  switch (state) {
    case 'requested': {
      return {
        icon: 'hourglass_empty',
        type: 'warning',
        message: 'Pending approval',
        color: 'warning',
      };
    }

    case 'approved': {
      return {
        icon: 'check',
        type: 'success',
        message: 'Approved',
        color: 'success',
      };
    }

    case 'rejected': {
      return {
        icon: 'close',
        type: 'danger',
        message: 'Rejected',
        color: 'danger',
      };
    }
  }
}
